import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { TickIcon } from "../tick-icon";
import { StatusModal } from "../status-modal/status-modal";
import { requestAccessTypeToken } from "../auth-client";
import { getAuthStatus } from "../../../helper/datalayer-utils";
import { applyPlanSectionStyles, getPlanDiscountText, getPlanButtonText, getPlanDuration, getPlanFeatures, getPlanOriginalPrice, getPlanPrice, getPlanTagline, getPlanTitle, getIsPopularPlan } from "../utils";
import "./plan-section.m.css";

const PlanSection = ({ plans = [], planCard = {}, colors = {}, campaign = {}, initAccessType, getSubscriptionForUser }) => {
  const sectionRef = useRef(null), [loadingPlanId, setLoadingPlanId] = useState(null), [modalData, setModalData] = useState(null), isInit = useRef(false);
  const { isLogin, config, user } = useSelector(s => {
    const u = s.user?.user?.userData || s.user?.userData || s.user, hasU = !!(u?.id || u?.email || u?.name);
    return { isLogin: s.isLogin || hasU, config: get(s, "qt.config.publisher-attributes"), user: hasU ? u : null };
  });

  const ensureAT = async () => {
    if (window.AccessType?.setUser) return true;
    return new Promise(r => { let a = 0; const c = setInterval(() => { if (window.AccessType?.setUser || ++a > 100) { clearInterval(c); r(!!window.AccessType?.setUser); } }, 100); if (!isInit.current) { isInit.current = true; initAccessType(() => { if (window.AccessType?.setUser) { clearInterval(c); r(true); } }); } });
  };

  const syncUser = async () => {
    console.log("[PlanSection] Syncing user with AccessType...");
    await ensureAT();
    const token = await requestAccessTypeToken(""), email = user?.email || user?.emails?.[0], phone = user?.phone || user?.phoneNumbers?.[0] || user?.mobile;
    console.log("[PlanSection] AccessType token obtained:", token);
    await window.AccessType.setUser({ emailAddress: email, mobileNumber: phone, accesstypeJwt: token });
  };

  const handleCtaClick = async (p) => {
    if (window) {
      const { status, uuid } = getAuthStatus();
      const formattedStatus = status === "not_logged_in" ? "non_logged_in" : status;

      const durationUnit = p.duration_unit || "year";
      const durationLength = p.duration_length || 1;
      let planName = `${durationLength}-${durationUnit.toLowerCase().replace(/s$/, "")}`;
      if (planName === "1-month") planName = "monthly";

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "subscription_plan_selection",
        auth_status: formattedStatus,
        uuid: uuid,
        plan_name: p.title || planName,
        plan_price: p.price_cents ? p.price_cents / 100 : "NA",
        currency: p.price_currency || "INR"
      });
    }

    const id = p.id || p.slug;
    if (!isLogin) {
      localStorage.setItem("pending_plan_id", id);
      let cleanUrl = window.location.href;
      cleanUrl = cleanUrl.split("?access_token=")[0].split("&access_token=")[0].split("?refresh_token=")[0].split("&refresh_token=")[0];
      cleanUrl = cleanUrl.replace(/[?&]$/, "");

      sessionStorage.setItem("sso_login_initiated_source", "plan_selection");
      localStorage.removeItem("last_logged_in_uuid");

      setTimeout(() => {
        window.location.href = `${config?.sso_endpoint || config?.sso_endpoint_server}/auth?redirectUrl=${encodeURIComponent(cleanUrl)}`;
      }, 300);
      return;
    }
    setLoadingPlanId(id);
    try {
      await syncUser();
      const subs = await getSubscriptionForUser(), sList = Array.isArray(subs) ? subs : (subs?.subscriptions || []);
      const active = sList.find(s => s.status === "active");
      if (active) return setModalData({ type: "active", redirectUrl: campaign?.["active-redirection"] || "/" });

      const opts = await window.AccessType.getPaymentOptions(), buildP = (p) => ({ type: "standard", plan: { id: p.id, title: p.title, price_cents: p.price_cents }, payment: { payment_type: (p.recurring || p.subscription_type === "recurring" || p.metadata?.recurring) ? "razorpay_recurring" : "razorpay", amount_cents: p.discounted_price_cents ?? p.price_cents, amount_currency: p.price_currency } });
      const res = await (opts?.data || opts)?.razorpay?.proceed(buildP(p));
      if (res && campaign?.["success-redirection"]) window.location.href = campaign["success-redirection"];
    } catch (e) {} finally { setLoadingPlanId(null); }
  };

  useEffect(() => { if (isLogin && !window.AccessType) ensureAT(); }, [isLogin]);
  useEffect(() => {
    const pending = localStorage.getItem("pending_plan_id");
    if (isLogin && plans.length && user && pending) {
      const target = plans.find(p => String(p.id) === String(pending));
      if (target) { localStorage.removeItem("pending_plan_id"); handleCtaClick(target); }
    }
  }, [isLogin, plans.length, !!user]);

  useEffect(() => { applyPlanSectionStyles(sectionRef.current, planCard, colors); }, [planCard, colors, plans]);
  if (!plans.length) return null;

  return (
    <>
      <section styleName="plans-section" ref={sectionRef}>
        <div styleName="plans-list">
          {plans.map((p, i) => {
            const id = p.id || p.slug || i;
            return (
              <article styleName="plan-card" key={id} data-plan-card style={p.plan_card_color ? { backgroundColor: p.plan_card_color } : {}}>
                {getIsPopularPlan(p) && <span styleName="popular-badge" data-popular-badge>{getIsPopularPlan(p)}</span>}
                <div styleName="plan-header">
                  <h2 styleName="plan-title" data-plan-title>{getPlanTitle(p)}</h2>
                  <div styleName="price-row"><span styleName="plan-price" data-plan-price>₹{getPlanPrice(p)}</span><span styleName="plan-duration" data-plan-duration>{getPlanDuration(p)}</span></div>
                </div>
                <div styleName="tagline-row"><p styleName="plan-tagline" data-plan-tagline>{getPlanTagline(p)}</p>
                  {(getPlanOriginalPrice(p) || getPlanDiscountText(p)) && <div styleName="price-meta-row">{getPlanOriginalPrice(p) && <span styleName="plan-original-price">₹{getPlanOriginalPrice(p)}</span>}
                  {getPlanDiscountText(p) && <span styleName="plan-discount-text" data-plan-discount-text>{getPlanDiscountText(p)}</span>}</div>}
                </div>
                <div styleName="feature-list">{getPlanFeatures(p).map((f, fi) => <div styleName="feature-row" key={fi}><TickIcon /><span styleName="feature-text" data-feature-text>{f}</span></div>)}</div>
                <button styleName="plan-button" data-plan-button type="button" disabled={loadingPlanId === id} onClick={() => handleCtaClick(p)}>{loadingPlanId === id ? "Loading..." : getPlanButtonText(p)}</button>
              </article>
            );
          })}
        </div>
      </section>
      {modalData && <StatusModal {...modalData} onClose={() => setModalData(null)} />}
    </>
  );
};

export { PlanSection };
